import { MAX_EXTRACTED_TEXT } from "./types";

// Extract text content from document files.
// Returns null for images (vision handles them) or on extraction failure.
export async function extractText(
  blob: Blob,
  mediaType: string,
): Promise<string | null> {
  try {
    if (mediaType === "text/plain" || mediaType === "text/markdown") {
      return truncate(await blob.text());
    }

    if (mediaType === "application/pdf") {
      return await extractPdfText(blob);
    }

    if (mediaType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
      return await extractDocxText(blob);
    }

    // Images — no text extraction
    return null;
  } catch (err) {
    console.error("[extract] failed:", err);
    return null;
  }
}

async function extractPdfText(blob: Blob): Promise<string | null> {
  const { extractText: pdfExtract } = await import("unpdf");
  const buffer = await blob.arrayBuffer();
  const { text } = await pdfExtract(new Uint8Array(buffer));
  // unpdf returns text as string[] (one entry per page)
  const joined = Array.isArray(text) ? (text as string[]).join("\n") : (text as string);
  if (!joined?.trim()) return null;
  return truncate(joined);
}

async function extractDocxText(blob: Blob): Promise<string | null> {
  // DOCX is a ZIP containing word/document.xml
  // Use DecompressionStream to unzip, parse XML for text nodes
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // Find word/document.xml in the ZIP
  const docXml = await extractFileFromZip(bytes, "word/document.xml");
  if (!docXml) return null;

  // Parse XML text content — strip tags, keep text nodes
  const text = docXml
    .replace(/<w:br[^>]*\/>/gi, "\n")           // line breaks
    .replace(/<\/w:p>/gi, "\n")                    // paragraph ends
    .replace(/<[^>]+>/g, "")                       // strip all tags
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/\n{3,}/g, "\n\n")                    // collapse blank lines
    .trim();

  if (!text) return null;
  return truncate(text);
}

// Minimal ZIP extraction — finds a specific file entry by name.
// Hardened against: infinite loops (entrySize=0, max iterations), out-of-bounds reads,
// data descriptor entries (bit 3), and ZIP bombs (decompression cap).
const MAX_ZIP_ENTRIES = 1000;

async function extractFileFromZip(zipBytes: Uint8Array, targetName: string): Promise<string | null> {
  const PK = [0x50, 0x4b, 0x03, 0x04];
  let offset = 0;
  let iterations = 0;

  while (offset < zipBytes.length - 30 && iterations < MAX_ZIP_ENTRIES) {
    iterations++;

    // Check for local file header signature
    if (
      zipBytes[offset] !== PK[0] || zipBytes[offset + 1] !== PK[1] ||
      zipBytes[offset + 2] !== PK[2] || zipBytes[offset + 3] !== PK[3]
    ) {
      break;
    }

    const generalFlags = zipBytes[offset + 6] | (zipBytes[offset + 7] << 8);
    const hasDataDescriptor = (generalFlags & 0x08) !== 0;
    const compressionMethod = zipBytes[offset + 8] | (zipBytes[offset + 9] << 8);
    const compressedSize = zipBytes[offset + 18] | (zipBytes[offset + 19] << 8) | (zipBytes[offset + 20] << 16) | (zipBytes[offset + 21] << 24);
    const uncompressedSize = zipBytes[offset + 22] | (zipBytes[offset + 23] << 8) | (zipBytes[offset + 24] << 16) | (zipBytes[offset + 25] << 24);
    const nameLen = zipBytes[offset + 26] | (zipBytes[offset + 27] << 8);
    const extraLen = zipBytes[offset + 28] | (zipBytes[offset + 29] << 8);

    const nameBytes = zipBytes.slice(offset + 30, offset + 30 + nameLen);
    const fileName = new TextDecoder().decode(nameBytes);

    const dataStart = offset + 30 + nameLen + extraLen;

    // Skip entries with data descriptors — sizes in the header are unreliable
    if (hasDataDescriptor) {
      // Can't reliably determine entry size; skip this file
      // (word/document.xml in well-formed DOCX won't use data descriptors)
      offset = dataStart;
      continue;
    }

    // Bounds check: data must fit within the zip
    if (dataStart + compressedSize > zipBytes.length) break;

    if (fileName === targetName) {
      const fileData = zipBytes.slice(dataStart, dataStart + compressedSize);

      if (compressionMethod === 0) {
        return new TextDecoder().decode(fileData);
      }

      if (compressionMethod === 8) {
        const ds = new DecompressionStream("deflate-raw");
        const writer = ds.writable.getWriter();
        writer.write(fileData);
        writer.close();

        const reader = ds.readable.getReader();
        const chunks: Uint8Array[] = [];
        let totalSize = 0;
        const MAX_DECOMPRESSED = 10 * 1024 * 1024; // 10MB safety cap

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          totalSize += value.byteLength;
          if (totalSize > MAX_DECOMPRESSED) {
            throw new Error("Decompressed file too large");
          }
          chunks.push(value);
        }

        const result = new Uint8Array(totalSize);
        let pos = 0;
        for (const chunk of chunks) {
          result.set(chunk, pos);
          pos += chunk.byteLength;
        }
        return new TextDecoder().decode(result);
      }

      return null; // Unsupported compression
    }

    // Advance to next entry — guard against zero-size entries (infinite loop)
    const entrySize = compressedSize > 0 ? compressedSize : uncompressedSize;
    if (entrySize === 0) break;
    offset = dataStart + entrySize;
  }

  return null;
}

function truncate(text: string): string {
  if (new TextEncoder().encode(text).length <= MAX_EXTRACTED_TEXT) {
    return text;
  }
  // Binary search for byte boundary
  let end = text.length;
  while (new TextEncoder().encode(text.slice(0, end)).length > MAX_EXTRACTED_TEXT - 20) {
    end = Math.floor(end * 0.9);
  }
  return text.slice(0, end) + "\n\n[... truncated ...]";
}
